<!--
╔════════════════════════════════════════════════════════════════════╗
║  SectionDocumentHtml.vue - Sezione Documenti Completa              ║
╚════════════════════════════════════════════════════════════════════╝

DESCRIZIONE:
Sezione avanzata per visualizzazione di documenti downloadabili,
pagine HTML, carosello immagini e link esterni. Supporta caricamento
progressivo con distinzione tra documenti correnti e storici.

FEATURES:
  ✓ Carosello immagini con frecce e puntini
  ✓ Filtraggio placeholder images
  ✓ Lista documenti con download
  ✓ Caricamento progressivo + "Storico"
  ✓ Link esterni separati
  ✓ HTML content sanitizzato
  ✓ Distinzione documenti storici

LAYOUT:
  - Header: eyebrow + titolo + carosello immagini
  - Corpo: testo HTML
  - Tabella documenti (con anno opzionale)
  - Tabella link esterni

LOGICA DOCUMENTI:
  - currentDocuments: documenti non storici
  - historicalDocuments: documenti con historical=1
  - visibleCount: controllo del "Carica altri"
  - Pulsante "Storico" quando presenti

DIPENDENZE:
  - sanitizeHtmlRegex
-->

<template>
  <section class="section-document">
    <div class="container">
      
      <div class="section-header">
        <p v-if="content.eyebrow" class="eyebrow">{{ content.eyebrow }}</p>
        <h2 class="title">{{ content.title }}</h2>

        <div v-if="shouldShowCarousel" class="document-image-carousel">
          
          <div v-if="isMultipleImages" class="carousel-container">
            <button 
              v-show="carouselIndex > 0"
              class="carousel-arrow carousel-arrow-left" 
              @click="carouselIndex--"
              aria-label="Immagine precedente"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="carousel-image-wrapper">
              <img 
                :src="currentCarouselImage" 
                :alt="content.title || 'Immagine'" 
                class="carousel-image"
              />
            </div>

            <button 
              v-show="carouselIndex < documentImages.length - 1"
              class="carousel-arrow carousel-arrow-right" 
              @click="carouselIndex++"
              aria-label="Immagine successiva"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div v-else class="single-image-wrapper">
            <img 
              :src="currentCarouselImage" 
              :alt="content.title || 'Immagine'" 
              class="carousel-image"
            />
          </div>

          <div v-if="isMultipleImages" class="carousel-dots">
            <button
              v-for="(_, index) in documentImages"
              :key="index"
              class="dot"
              :class="{ active: carouselIndex === index }"
              @click="carouselIndex = index"
              :aria-label="`Immagine ${index + 1}`"
            />
          </div>
        </div>

        <div
          v-if="content.text"
          class="html-content"
          v-html="sanitizeHtml(content.text)"
        ></div>
      </div>

      <div v-if="content.files?.length" class="document-list">
        <div
          v-for="(item, index) in visibleDocuments"
          :key="index"
          class="document-item"
          :class="{ 'no-year': !hasYearColumn }"
          @click="handleDownloadClick($event, item.fileUrl)"
        >
          <div v-if="hasYearColumn" class="document-year">{{ item.year }}</div>
          <h3 class="document-title">{{ item.label }}</h3>
          <div class="document-size">{{ item.size }}</div>
          <div class="document-icon">
            <img :src="item.icon || '/images/logos/pdf_icon.png'" alt="PDF" />
          </div>
        </div>

        <div v-if="hasMoreDocuments" class="load-more-container">
          <button
            @click="loadMoreDocuments"
            class="load-more-button"
            :disabled="isLoading"
          >
            <span class="btn-label">{{ isLoading ? 'Caricamento…' : getLoadMoreText() }}</span>
            <span class="arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </div>

      <div v-if="content.links?.length" class="document-list links-section">
        <div
          v-for="(link, index) in content.links"
          :key="'link-' + index"
          class="document-item link-item"
          @click="handleLinkClick($event, link.url)"
        >
          <h3 class="document-title">{{ link.titolo }}</h3>
          <div class="document-size">link</div>
          <div class="document-icon">
            <img :src="link.icon || '/images/logos/external_link.svg'" alt="Link" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { sanitizeHtmlRegex } from '@/utils/sanitizeHtml'

// ──────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ──────────────────────────────────────────────────────────────
const props = defineProps({
  /**
   * Contenuto della sezione
   * @type {Object}
   * @required
   * @property {string} eyebrow - Testo eyebrow
   * @property {string} title - Titolo principale
   * @property {string} text - Testo HTML
   * @property {string|Array} image - URL/Array immagini
   * @property {Array} files - Documenti downloadabili
   * @property {Array} links - Link esterni
   */
  content: {
    type: Object,
    required: true
  }
})

// ──────────────────────────────────────────────────────────────
// CAROUSEL LOGIC - Logica carosello immagini
// ──────────────────────────────────────────────────────────────
const carouselIndex = ref(0)

/**
 * Estrae il basename da un URL o percorso
 * Usato per riconoscere placeholder images
 * @param {string} src - URL o percorso
 * @returns {string} basename del file
 */
function getBasename (src) {
  try {
    const u = new URL(src, window.location.origin)
    const cleanPath = u.pathname
    return cleanPath.substring(cleanPath.lastIndexOf('/') + 1)
  } catch (e) {
    const noQuery = String(src).split('?')[0].split('#')[0]
    const parts = noQuery.split('/')
    return parts[parts.length - 1] || noQuery
  }
}

/**
 * Regex per identificare placeholder images
 * Formato: placeholder_[news|product|document][_numero].ext
 */
const PLACEHOLDER_RE = /^placeholder[_-]?(?:news|product|document)(?:_\d+)?\.(png|jpg|jpeg|webp)$/i

/**
 * Verifica se un'immagine è un placeholder
 * @param {string} src - URL immagine
 * @returns {boolean} true se è placeholder
 */
function isPlaceholderImage (src) {
  const name = getBasename(src)
  return PLACEHOLDER_RE.test(name)
}

/**
 * Array immagini valide (non placeholder)
 */
const documentImages = computed(() => {
  if (!props.content?.image) return []
  const imgs = Array.isArray(props.content.image) ? props.content.image : [props.content.image]
  return imgs
})

/**
 * Verifica se sono presenti multiple immagini
 */
const isMultipleImages = computed(() => {
  return documentImages.value.length > 1
})

/**
 * Verifica se mostrare carosello (solo se non tutti placeholder)
 */
const shouldShowCarousel = computed(() => {
  if (documentImages.value.length === 0) return false
  return !documentImages.value.every(img => isPlaceholderImage(img))
})

/**
 * Immagine corrente del carosello
 */
const currentCarouselImage = computed(() => {
  if (documentImages.value.length === 0) return null
  const img = documentImages.value[carouselIndex.value]
  return img || null
})

/**
 * Resetta carosello al cambio content
 */
watch(() => props.content, () => {
  carouselIndex.value = 0
})

// ──────────────────────────────────────────────────────────────
// DOCUMENTS LOADING LOGIC - Caricamento progressivo documenti
// ──────────────────────────────────────────────────────────────
// const visibleCount = ref(5)  // [COMMENTATO] Tutti i documenti sono ora visibili
const isLoading = ref(false)
// const documentsPerLoad = 10  // [COMMENTATO] Logica caricamento progressivo rimossa
const showHistorical = ref(false)

/**
 * Verifica se presenti documenti storici
 */
const hasHistoricalDocuments = computed(() => {
  return props.content?.files?.some(item => item.historical === 1) || false
})

/**
 * Documenti correnti (non storici)
 */
const currentDocuments = computed(() => {
  return props.content?.files?.filter(item => !item.historical || item.historical === 0) || []
})

/**
 * Documenti storici
 */
const historicalDocuments = computed(() => {
  return props.content?.files?.filter(item => item.historical === 1) || []
})

/**
 * Verifica se almeno un documento ha l'anno
 */
const hasYearColumn = computed(() => {
  return props.content?.files?.some(item => item.year) || false
})

/**
 * Documenti visibili: applica logica storico e caricamento progressivo
 * [MODIFICATO] Caricamento progressivo rimosso - mostra sempre tutti i documenti correnti
 */
const visibleDocuments = computed(() => {
  if (hasHistoricalDocuments.value) {
    if (showHistorical.value) {
      return props.content?.files || []
    } else {
      return currentDocuments.value
    }
  }
  // [COMMENTATO] Logica caricamento progressivo rimossa - mostra tutti i documenti
  // return props.content?.files?.slice(0, visibleCount.value) || []
  return props.content?.files || []
})

/**
 * Verifica se ci sono altri documenti da caricare
 * [MODIFICATO] Solo per documenti storici - caricamento progressivo rimosso
 */
const hasMoreDocuments = computed(() => {
  // [COMMENTATO] Logica caricamento progressivo rimossa
  // if (hasHistoricalDocuments.value) {
  //   return !showHistorical.value && historicalDocuments.value.length > 0
  // }
  // const totalDocuments = props.content?.files?.length || 0
  // return visibleCount.value < totalDocuments
  
  // Mostra pulsante "Storico" solo se ci sono documenti storici e non sono ancora visibili
  return hasHistoricalDocuments.value && !showHistorical.value && historicalDocuments.value.length > 0
})

/**
 * Testo del pulsante "Carica altri" / "Storico"
 * [MODIFICATO] Solo "Storico" disponibile ora
 */
const getLoadMoreText = () => {
  // [COMMENTATO] Logica caricamento progressivo rimossa
  // if (hasHistoricalDocuments.value) {
  //   return 'Storico'
  // }
  // const totalDocuments = props.content?.files?.length || 0
  // const remaining = totalDocuments - visibleCount.value
  // 
  // if (remaining <= documentsPerLoad) {
  //   return `Carica gli ultimi ${remaining} documenti`
  // }
  // return `Carica altri ${documentsPerLoad} documenti`
  
  return 'Storico'
}

/**
 * Carica altri documenti o mostra storico
 * [MODIFICATO] Solo logica "Storico" mantenuta
 */
const loadMoreDocuments = async () => {
  isLoading.value = true
  
  // Simula delay di caricamento
  await new Promise(resolve => setTimeout(resolve, 300))
  
  // [COMMENTATO] Logica caricamento progressivo rimossa
  // if (hasHistoricalDocuments.value) {
  //   showHistorical.value = true
  // } else {
  //   const totalDocuments = props.content?.files?.length || 0
  //   const newCount = Math.min(visibleCount.value + documentsPerLoad, totalDocuments)
  //   visibleCount.value = newCount
  // }
  
  // Mostra documenti storici
  showHistorical.value = true
  
  isLoading.value = false
}

// ──────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ──────────────────────────────────────────────────────────────

/**
 * Gestisce click su documento (apertura nuova scheda)
 */
function handleDownloadClick(event, link) {
  window.open(link, '_blank')
}

/**
 * Gestisce click su link esterno/interno
 */
function handleLinkClick(event, url) {
  if (url && url.startsWith('/')) {
    window.location.href = url
  } else {
    window.open(url, '_blank')
  }
}

/**
 * Sanitizza HTML
 */
function sanitizeHtml(html) {
  return sanitizeHtmlRegex(html)
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/partials/variables';
@import "@/assets/styles/partials/compat";
@import '@/assets/styles/partials/typography';

/* ------------------------------------------------------------------
   Stili base della sezione documenti
------------------------------------------------------------------ */
.section-document {
  padding: $section-padding-y $space-1 $space-6 $space-1;
  max-width: 100vw;
  overflow-x: hidden;

  .container {
    max-width: $container-max-width;
    margin: 0 auto;
  }
}

/* ------------------------------------------------------------------
   Header con eyebrow, titolo e descrizione HTML
------------------------------------------------------------------ */
.section-header {
  margin-bottom: clamp(2rem, 4vw, 3rem);

  .eyebrow {
    position: relative;
    display: inline-block;
    font-size: $font-eyebrow;
    font-weight: 600;
    text-transform: uppercase;
    color: $clr-tertiary;
    margin-bottom: $space-2;
    margin-left: 60px;
    cursor: default;
    white-space: nowrap;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -100px;
      width: 40%;
      height: 12px;
      background-color: rgba($clr-tertiary, 0.7);
      transition: all 0.3s ease;
    }

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;

        &::before {
          background: rgba($clr-tertiary, 0.8);
          width: 140%;
          transform: scaleY(1.2);
        }
      }
    }
  }

  .title {
    font-size: $font-title;
    font-weight: 300;
    margin: 0;
    color: $text-color;
    margin-left: 60px;
    margin-bottom: $space-2;
  }

  /* Immagine hero - CENTRATA */
  .hero-image {
    width: 100%;
    margin: $space-2 auto $space-3 auto;
    text-align: center;

    img {
      width: 100%;
      max-width: 600px;
      height: auto;
      display: inline-block;
      box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
      transition: all 0.3s ease;

      @media (hover: hover) {
        &:hover {
          transform: scale(1.02);
          box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
        }
      }
    }
  }

  /* ------------------------------------------------------------------
     Carosello Immagini
  ------------------------------------------------------------------ */
  .document-image-carousel {
    width: 100%;
    margin: $space-2 auto $space-3 auto;
    text-align: center;
  }

  .carousel-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    @include flex-gap(1rem);
    width: 100%;
    overflow: visible;
  }

  .carousel-image-wrapper,
  .single-image-wrapper {
    width: 100%;
    max-width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 auto;
    min-width: 0;
  }

  .carousel-image {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 500px;
    display: inline-block;
    box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
    transition: all 0.3s ease;

    @media (hover: hover) {
      &:hover {
        transform: scale(1.02);
        box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
      }
    }
  }

  .carousel-arrow {
    position: absolute;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba($clr-brand-dark, 0.1);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    z-index: 10;
    flex-shrink: 0;
    outline: none;
    padding: 0;

    svg {
      width: 24px;
      height: 24px;
      color: $clr-brand-dark;
    }

    @media (hover: hover) {
      &:hover {
        background-color: rgba(255, 255, 255, 1);
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.15);
      }
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }

    /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
       scartata per intero. Si ripiega su :focus, che mostra l'anello
       anche col mouse: e' il degrado accettato. */
    .no-focusvisible &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }

    &:active {
      transform: scale(0.98);
    }

    &.carousel-arrow-left {
      left: -3rem;
    }

    &.carousel-arrow-right {
      right: -3rem;
    }

    @media (max-width: 768px) {
      width: 50px;
      height: 50px;
      padding: 0;

      svg {
        width: 20px;
        height: 20px;
      }

      &.carousel-arrow-left {
        left: -2.5rem;
      }

      &.carousel-arrow-right {
        right: -2.5rem;
      }

      &:focus,
      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
      }

      /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
         scartata per intero. Si ripiega su :focus, che mostra l'anello
         anche col mouse: e' il degrado accettato. */
      .no-focusvisible &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
      }
    }

    @media (max-width: 480px) {
      width: 40px;
      height: 40px;
      padding: 0;

      svg {
        width: 18px;
        height: 18px;
      }

      &.carousel-arrow-left {
        left: 0.5rem;
      }

      &.carousel-arrow-right {
        right: 0.5rem;
      }

      &:focus,
      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
      }

      /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
         scartata per intero. Si ripiega su :focus, che mostra l'anello
         anche col mouse: e' il degrado accettato. */
      .no-focusvisible &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
      }
    }
  }

  .carousel-dots {
    display: flex;
    justify-content: center;
    @include flex-gap(0.75rem);
    margin-top: 1.5rem;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: none;
    background-color: rgba($clr-text-light, 0.3);
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 0;

    @media (hover: hover) {
      &:hover {
        background-color: rgba($clr-text-light, 0.5);
        transform: scale(1.2);
      }
    }

    &.active {
      background-color: $clr-tertiary;
      width: 24px;
      border-radius: 5px;
    }
  }

  .html-content {
    font-size: $font-base;
    line-height: 1.6;
    color: $text-color;
    margin-left: 60px;
    margin-top: $space-1;

    :deep(p) {
      margin: 0 0 $space-1 0;
      font-size: inherit;
      line-height: inherit;
      color: inherit;
    }

    :deep(strong) {
      font-weight: 700;
      color: $text-color;
    }

    :deep(em) {
      font-style: italic;
    }

    :deep(span) {
      font-size: inherit;
      line-height: inherit;
    }

    /* Link styling - no underline, tertiary green color */
    :deep(a) {
      color: $clr-fonda;
      text-decoration: none;
    }
  }
}

/* ------------------------------------------------------------------
   Lista documenti e links
------------------------------------------------------------------ */
.document-list {
  width: 100%;
  max-width: 1600px;
  padding: 0 clamp($space-0-5, 2vw, $space-1);
  overflow-x: hidden;
  box-sizing: border-box;
  margin: 0 auto;

  &.links-section {
    margin-top: $space-3;
    border-top: none;
  }

  .section-subtitle {
    font-size: $font-h4;
    font-weight: 600;
    color: $clr-brand-dark;
    margin-bottom: $space-1-5;
    margin-left: 0;
  }
}

/* ------------------------------------------------------------------
   Item singolo documento - Layout con year opzionale
------------------------------------------------------------------ */
.document-item {
  display: grid;
  grid-template-columns: clamp(100px, 15vw, 130px) minmax(0, 1fr) clamp(80px, 10vw, 100px) 40px;
  grid-template-areas: "year title size icon";
  align-items: center;
  gap: clamp(2rem, 3vw, 2.5rem);
  padding: clamp(1rem, 2.5vw, 1.5rem) 0;
  border-bottom: 1px solid $clr-border;
  transition: background-color 0.25s ease, transform 0.2s ease;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  /* Layout senza colonna year */
  &.no-year {
    grid-template-columns: minmax(0, 1fr) clamp(80px, 10vw, 100px) 40px;
    grid-template-areas: "title size icon";
  }

  /* Layout per links (senza size) */
  &.link-item {
    grid-template-columns: minmax(0, 1fr) auto 40px;
    grid-template-areas: "title size icon";
  }

  @media (hover: hover) {
    &:hover {
      background-color: rgba($clr-bg-light, 0.6);
      transform: translateX(4px);

      .document-title {
        color: $clr-brand-dark;
      }
    }
  }

  &:last-child {
    border-bottom: none;
  }

  .document-year {
    grid-area: year;
    font-size: $font-list-date;
    color: $clr-text-light;
    font-weight: 500;
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .document-title {
    grid-area: title;
    font-size: $font-list-title;
    font-weight: 600;
    color: $clr-tertiary;
    line-height: 1.4;
    margin: 0;
    cursor: pointer !important;
    transition: color 0.25s ease;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    @include line-clamp(2, 1.4, $overflow: false);
    word-break: break-word;
    overflow-wrap: break-word;
    padding-right: 1.5rem;
  }

  .document-size {
    grid-area: size;
    font-size: $font-list-meta;
    color: $clr-text-light;
    font-weight: 500;
    white-space: nowrap;
    text-align: right;
    margin-right: clamp(0.5rem, 1vw, 1rem);
  }

  .document-icon {
    grid-area: icon;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    align-self: start;

    img {
      width: clamp(20px, 3vw, 24px);
      height: clamp(20px, 3vw, 24px);
      object-fit: contain;
    }
  }
}

/* PDF più grande SOLO nella lista file (non nei link) */
.document-list:not(.links-section) .document-icon img {
  width: clamp(30px, 4vw, 36px);
  height: clamp(30px, 4vw, 36px);
}

/* Uniforma file/link sotto i 1200px */
@media (max-width: 1200px) {
  .document-list .document-icon img {
    width: 26px;
    height: 26px;
  }
}

/* ------------------------------------------------------------------
   Bottone Carica Altri - freccia + spacing ridotto
------------------------------------------------------------------ */
.load-more-container {
  width: 100%;
  max-width: 1600px;
  padding: 0 clamp($space-0-5, 2vw, $space-1);
  margin-top: clamp(1rem, 2.2vw, 1.5rem);
  display: flex;
  justify-content: flex-start;
  box-sizing: border-box;
}

.load-more-button {
  background: none;
  border: none;
  color: $clr-brand-dark;
  font-size: $font-list-meta;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  padding: clamp(0.75rem, 1.5vw, 1rem) clamp(1rem, 2vw, 1.5rem);
  border-radius: 4px;
  transition: all 0.3s ease;
  position: relative;
  display: inline-flex;
  align-items: center;
  @include flex-gap(0.5rem);

  .btn-label { line-height: 1; }

  .arrow {
    display: inline-flex;
    width: 1.05em;
    height: 1.05em;
    transform: rotate(180deg);
    transition: transform 0.25s ease;
    svg { width: 100%; height: 100%; display: block; }
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      color: $clr-tertiary;
      background-color: rgba($clr-tertiary, 0.1);
      transform: translateX(4px);
      .arrow { transform: rotate(360deg); }
    }
  }

  &:disabled {
    color: $clr-text-light;
    cursor: not-allowed;
    transform: none;
  }

  &:focus {
    outline: 2px solid $clr-tertiary;
    outline-offset: 2px;
  }
}

/* ------------------------------------------------------------------
   Media queries - Desktop medio-grande (1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .section-document {
    padding: $space-6 $space-0-5 $space-5 $space-0-5;

    .section-header {
      margin-bottom: clamp(1.5rem, 3vw, 2.5rem);

      .eyebrow {
        margin-left: 50px;
        font-size: $font-eyebrow-lg;

        &::before {
          height: 10px;
          left: -80px;
        }

        @media (hover: hover) {
          &:hover::before {
            width: calc(100% + 80px);
          }
        }
      }

      .title {
        font-size: $font-title-lg;
        margin-left: 50px;
      }

      .hero-image {
        margin: $space-2 auto $space-2-5 auto;
      }

      .html-content {
        font-size: $font-base-lg;
        margin-left: 50px;
      }
    }

    .document-list {
      padding: 0 clamp($space-0-5, 1.5vw, $space-0-75);
    }

    .load-more-container {
      padding: 0 clamp($space-0-5, 1.5vw, $space-0-75);
    }

    .document-item {
      grid-template-columns: clamp(90px, 12vw, 120px) minmax(0, 1fr) clamp(70px, 8vw, 90px) 35px;
      gap: clamp(1.5rem, 2.5vw, 2rem);
      padding: clamp(0.9rem, 2.2vw, 1.3rem) 0;

      &.no-year {
        grid-template-columns: minmax(0, 1fr) clamp(70px, 8vw, 90px) 35px;
      }

      .document-year {
        font-size: $font-list-date-lg;
      }

      .document-title {
        font-size: $font-list-title-lg;
      }

      .document-size {
        font-size: $font-list-meta-lg;
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries - Tablet landscape (1024px)
------------------------------------------------------------------ */
@media (max-width: 1024px) {
  .document-list {
    padding: 0 clamp($space-0-5, 1.2vw, $space-0-75);
  }

  .load-more-container {
    padding: 0 clamp($space-0-5, 1.2vw, $space-0-75);
  }

  .document-item {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      "year title icon"
      "size size size";
    gap: clamp(1.5rem, 2.5vw, 2rem);
    padding: clamp(0.8rem, 2vw, 1.2rem) 0;

    &.no-year {
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "title icon"
        "size size";
    }

    &.link-item {
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "title icon"
        "size size";
    }

    .document-size {
      margin-top: clamp(0.25rem, 1vw, 0.5rem);
      grid-column: 1 / -1;
      text-align: left;
      margin-right: 0;
    }

    .document-year {
      min-width: clamp(80px, 15vw, 100px);
    }
  }

  .document-list .document-icon img {
    width: 26px;
    height: 26px;
  }
}

/* ------------------------------------------------------------------
   Media queries - Tablet (920px)
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .section-document {
    padding: $space-4 $space-0-5 $space-3 $space-0-5;

    .section-header {
      .eyebrow {
        margin-left: 30px;
        margin-bottom: $space-0-5;
        font-size: $font-eyebrow-sm;

        &::before {
          height: 8px;
          left: -40px;
        }

        @media (hover: hover) {
          &:hover::before {
            width: calc(100% + 40px);
          }
        }
      }

      .title {
        font-size: $font-title-sm;
        margin-left: 30px;
        margin-bottom: $space-0-5;
      }

      .hero-image {
        margin: $space-2 auto;
      }

      .html-content {
        font-size: $font-base-sm;
        margin-left: 30px;
      }
    }

    .document-list {
      padding: 0 clamp($space-0-25, 1vw, $space-0-5);
    }

    .document-item {
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "title icon"
        "year year"
        "size size";
      gap: clamp(1.5rem, 2vw, 1.75rem);
      padding: clamp(0.7rem, 1.8vw, 1rem) 0;

      &.no-year {
        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-areas:
          "title icon"
          "size size";

        .document-icon {
          align-self: start;
          justify-self: end;
        }
      }

      &.link-item {
        grid-template-areas:
          "title icon"
          "size size";

        .document-icon {
          align-self: start;
        }
      }

      .document-year,
      .document-size {
        margin-top: clamp(0.2rem, 0.8vw, 0.4rem);
        grid-column: 1 / -1;
      }

      .document-year {
        font-size: $font-list-date-sm;
        min-width: auto;
      }

      .document-title {
        font-size: $font-list-title-sm;
        line-height: 1.3;
      }

      .document-size {
        font-size: $font-list-meta-sm;
      }

      .document-icon img {
        width: 22px;
        height: 22px;
      }
    }

    .document-list .document-icon img {
      width: 22px;
      height: 22px;
    }

    .load-more-button {
      font-size: $font-list-meta-sm;
    }
  }
}

/* ------------------------------------------------------------------
   Media queries - Mobile (640px)
------------------------------------------------------------------ */
@media (max-width: 640px) {
  .document-list {
    padding: 0 clamp($space-0-25, 0.8vw, $space-0-5);
  }

  .document-item {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "title icon"
      "year year"
      "size size";
    gap: clamp(1.5rem, 1.5vw, 1.75rem);
    padding: clamp(0.6rem, 1.5vw, 0.9rem) 0;

    &.no-year {
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "title icon"
        "size size";

      .document-icon {
        justify-self: end;
        align-self: start;
      }
    }

    &.link-item {
      grid-template-areas:
        "title icon"
        "size size";
    }

    .document-icon {
      justify-self: end;
      img {
        width: 20px;
        height: 20px;
      }
    }

    .document-year,
    .document-size {
      grid-column: 1 / -1;
      margin-top: clamp(0.2rem, 0.6vw, 0.4rem);
    }
  }

  .document-list .document-icon img {
    width: 20px;
    height: 20px;
  }
}

/* ------------------------------------------------------------------
   Media queries - Mobile (480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .section-document {
    padding: $space-3 $space-0-5 $space-2 $space-0-5;

    .section-header {
      .eyebrow {
        margin-left: 20px;
        margin-bottom: $space-0-25;
        font-size: $font-eyebrow-xs;

        &::before {
          height: 7px;
          left: -30px;
        }

        @media (hover: hover) {
          &:hover::before {
            width: calc(100% + 30px);
          }
        }
      }

      .title {
        font-size: $font-title-xs;
        margin-left: 20px;
        margin-bottom: $space-0-5;
      }

      .hero-image {
        margin: $space-1-5 auto;
      }

      .html-content {
        font-size: $font-base-xs;
        margin-left: 20px;
      }
    }

    .document-list {
      padding: 0 clamp($space-0-25, 0.6vw, $space-0-25);
    }

    .load-more-container {
      padding: 0 clamp($space-0-25, 0.6vw, $space-0-25);
      margin-top: clamp(0.75rem, 2vw, 1.5rem);
    }

    .document-item {
      padding: clamp(0.5rem, 1.2vw, 0.7rem) 0;
      gap: clamp(1rem, 1vw, 1.25rem);

      .document-year {
        font-size: $font-list-date-xs;
      }

      .document-title {
        font-size: $font-list-title-xs;
        line-height: 1.2;
        @include line-clamp-lines(3, 1.2);
      }

      .document-size {
        font-size: $font-list-meta-xs;
      }

      .document-icon img {
        width: 18px;
        height: 18px;
      }
    }

    .document-list .document-icon img {
      width: 18px;
      height: 18px;
    }

    .load-more-button {
      font-size: $font-list-meta-xs;
    }
  }
}

/* ------------------------------------------------------------------
   Media queries - Mobile piccolissimo (360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .document-list {
    padding: 0 $space-0-25;
  }

  .section-document {
    padding: $space-2 $space-0-5 $space-1-5 $space-0-5;

    .section-header {
      .eyebrow {
        margin-left: 15px;
        margin-bottom: $space-0-25;
        font-size: $font-eyebrow-xxs;

        &::before {
          height: 6px;
          left: -20px;
        }

        @media (hover: hover) {
          &:hover::before {
            width: calc(100% + 20px);
          }
        }
      }

      .title {
        font-size: $font-title-xxs;
        margin-left: 15px;
        margin-bottom: $space-0-5;
      }

      .hero-image {
        margin: $space-1-25 auto;
      }

      .html-content {
        font-size: $font-base-xxs;
        margin-left: 15px;
      }
    }

    .document-item {
      padding: clamp(0.4rem, 1vw, 0.6rem) 0;
      gap: clamp(1rem, 0.8vw, 1.25rem);

      .document-year {
        font-size: $font-list-date-xxs;
      }

      .document-title {
        font-size: $font-list-title-xxs;
        line-height: 1.1;
      }

      .document-size {
        font-size: $font-list-meta-xxs;
      }

      .document-icon img {
        width: 16px;
        height: 16px;
      }
    }

    .document-list .document-icon img {
      width: 16px;
      height: 16px;
    }

    .load-more-button {
      font-size: $font-list-meta-xxs;
    }
  }
}
</style>